import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import FormFieldError from '~/components/atoms/FormFieldError/FormFieldError'
import Link from '~/components/atoms/Link/Link'
import Loader from '~/components/atoms/Loader/Loader'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TextInput from '~/components/atoms/TextInput/TextInput'
import PhoneNumberInput from '~/components/forms/PhoneNumberInput/PhoneNumberInput'

import { useMagazineAdvertForm } from './hooks'

import styles from './MagazineAdvertForm.module.css'
import authStyles from '~/components/blocks/AuthDialog/AuthDialog.module.css'

export interface MagazineAdvertFormProps {
  testID?: string
  defaultValues?: any
  currentTab?: string | null
  formOpen?: boolean
}

export default function MagazineAdvertForm({
  testID,
  defaultValues,
  formOpen,
}: MagazineAdvertFormProps) {
  const { formik, errorMessage, isLoading, success } = useMagazineAdvertForm(
    defaultValues,
    formOpen
  )

  const updateMobileNumber = (name: string, value: string) => {
    formik.setFieldValue(name, value)
  }

  const loading = () => {
    return (
      <>
        <Spacer size={SpacerSizes.lg} />
        <Loader />
      </>
    )
  }

  return (
    <div className={styles.base} data-testid={testID}>
      {success && (
        <Text Tag="p" size={TextSizes.bodyMed} className={styles.success}>
          Thank you, your interest has been submitted.
        </Text>
      )}

      {isLoading ? (
        loading()
      ) : (
        <>
          {errorMessage && (
            <>
              <Text
                Tag="p"
                size={TextSizes.bodySml}
                className={authStyles.formError}
              >
                {errorMessage}
              </Text>
              <Spacer size={SpacerSizes.sm} />
            </>
          )}
          {!success && (
            <form onSubmit={formik.handleSubmit} className={styles.form}>
              {!defaultValues && (
                <>
                  <div className={styles.formField}>
                    <TextInput
                      value={formik.values.firstname}
                      label="First name*"
                      onChange={formik.handleChange}
                      name="firstname"
                      onBlur={formik.handleBlur}
                      disabled={isLoading}
                    />
                    {formik.errors.firstname && formik.touched.firstname && (
                      <FormFieldError
                        message={formik.errors.firstname}
                        absolutePos={false}
                      />
                    )}
                  </div>

                  <div className={styles.formField}>
                    <TextInput
                      value={formik.values.lastname}
                      label="Last name*"
                      onChange={formik.handleChange}
                      name="lastname"
                      onBlur={formik.handleBlur}
                      disabled={isLoading}
                    />
                    {formik.errors.lastname && formik.touched.lastname && (
                      <FormFieldError
                        message={formik.errors.lastname}
                        absolutePos={false}
                      />
                    )}
                  </div>

                  <div className={styles.formField}>
                    <TextInput
                      value={formik.values.email}
                      label="Email*"
                      onChange={formik.handleChange}
                      name="email"
                      onBlur={formik.handleBlur}
                      disabled={isLoading}
                    />
                    {formik.errors.email && formik.touched.email && (
                      <FormFieldError
                        message={formik.errors.email}
                        absolutePos={false}
                      />
                    )}
                  </div>

                  <div
                    className={classNames(styles.formField, styles.phoneField)}
                  >
                    <PhoneNumberInput
                      value={formik.values.phoneNumber}
                      placeholder="Mobile number*"
                      onChange={updateMobileNumber}
                      name="phoneNumber"
                      onBlur={formik.handleBlur}
                      disabled={isLoading}
                    />
                    {formik.errors.phoneNumber &&
                      formik.touched.phoneNumber && (
                        <FormFieldError
                          message={formik.errors.phoneNumber}
                          absolutePos={false}
                        />
                      )}
                  </div>
                </>
              )}

              <Spacer size={SpacerSizes.sm} />

              <div className={styles.buttonContainer}>
                <div className={styles.signupButton}>
                  <Button
                    variant={ButtonVariants.primaryLight}
                    disabled={
                      formik.isSubmitting || !formik.isValid || isLoading
                    }
                    button={{ type: ButtonTypes.submit }}
                  >
                    Enquire
                  </Button>
                </div>
              </div>

              <Text size={TextSizes.bodyMed} className={styles.terms}>
                MyArtBroker.com needs the contact information you provide to us
                to contact you about our products and services. You may
                unsubscribe from these communications at any time. For
                information on how to unsubscribe, as well as our privacy
                practices and commitment to protecting your privacy, please
                review our{' '}
                <Link target="_blank" href="/privacy">
                  Privacy Policy
                </Link>
              </Text>
            </form>
          )}
        </>
      )}
    </div>
  )
}
